import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  useOutsideClick,
  useDisclosure,
} from "@chakra-ui/react";
import { Canvas } from "@react-three/fiber";
import RotatingText from "../components/General/RotatingText";
import HeroSection from "../components/Home/HeroSection";
import TeamSection from "../components/Home/TeamSection";
import PromessesSection from "../components/Home/PromessesSection";
import MissionSection from "../components/Home/MissionSection";
import AssetsSection from "../components/Home/AssetsSection";
import ModelsSection from "../components/Home/ModelsSection";
import Banner from "../components/General/Banner";
import ActiveCardModalDisplay from "../components/Home/ActiveCardModalDisplay";
import FAQ from "../components/Home/FAQ";
import { texts } from '../lang';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../globalvariables';

const Home = () => {
  const { language } = useLanguage();
  const homeTexts = texts[language].home;
  const navigate = useNavigate();

  const [activeCard, setActiveCard] = useState(null);
  const [rotationOpacity, setRotationOpacity] = useState(0);
  const [activeCofounderModal, setActiveCofounderModal] = useState(null);

  const containerRef = useRef();
  const heroRef = useRef(); 

  useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      const ratio = 1 - entry.intersectionRatio;
        setRotationOpacity(Math.min(Math.max(ratio * 0.07, 0), 0.07));
    },
    {
      threshold: Array.from({ length: 11 }, (_, i) => i * 0.1),
    }
  );

  if (heroRef.current) {
    observer.observe(heroRef.current);
  }

  return () => {
    if (heroRef.current) {
      observer.unobserve(heroRef.current);
    }
  };
}, []);

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (activeCard) setActiveCard(null);
    },
  });

  const isOverlay = Boolean(activeCard);
  
  const handleMachineExplore = () => {
    navigate('/machine');
  };

  return (
    <Box width="100%">
      <Box
        width="100%"
        bg={COLORS.background}
        overflowY="auto"
        position="relative"
      >
        <Box
          position="fixed" top={0} left={0} w="100%" h="100vh" zIndex={0}
          bg={`radial-gradient(circle at 50% 50%, ${COLORS.background}DD, ${COLORS.background} 70%)`}
        >
        </Box>

        <VStack
          spacing={24}
          position="relative"
          zIndex={10}
          pt={0}
          pb={32}
          px={4}
          bg="transparent"
          opacity={isOverlay ? 0.3 : 1}
          transition="opacity 0.3s ease"
        >
          <Box ref={heroRef} display={activeCofounderModal ? "none" : "block"}>
            <HeroSection 
              title={homeTexts.heroTitle} 
              subtitle={homeTexts.heroSubtitle} 
              buttonText={homeTexts.heroButton}
              phrases={homeTexts.heroPhrases}
              activeCofounderModal={activeCofounderModal}
             //logoName={homeTexts.heroLogoName}
            />
          </Box>

          <Box display={activeCofounderModal ? "none" : "block"} w="100%" maxW={{ base: "100%", md: "90%" }} mx="auto">
            <Banner videoSrc={homeTexts.videoSrc} borderRadius="16px" />
          </Box>

          <Box display={activeCofounderModal ? "none" : "block"}>
            <MissionSection 
              title={homeTexts.missionTitle}
              missionParts={homeTexts.missionParts}
              previousButtonText={homeTexts.missionPreviousButton}
              nextButtonText={homeTexts.missionNextButton}
            /> 
          </Box>
          
          <Box display={activeCofounderModal ? "none" : "block"}>
            <AssetsSection 
              title={homeTexts.valuesTitle}
              valuesData={homeTexts.valuesSectionValues}
            /> 
          </Box>
         
          <Box display={activeCofounderModal ? "none" : "block"}>
            <PromessesSection 
              title={homeTexts.partnersTitle}
              partnersData={homeTexts.partnersSectionData}
              tabTexts={{
                stores: homeTexts.partnersTabStores,
                designers: homeTexts.partnersTabDesigners,
                recycling: homeTexts.partnersTabRecycling
              }}
            />
          </Box>
          
          <Box display={activeCofounderModal ? "none" : "block"}>
            <ModelsSection 
              title={texts.fr.models.title}
              models={texts.fr.models.items}
            />
          </Box>

          <Box id="team-section">
            <TeamSection 
              title={homeTexts.teamTitle}
              teamMembersData={homeTexts.teamSectionMembers}
              onActiveModalChange={setActiveCofounderModal}
            /> 
          </Box> 
          
          <Box display={activeCofounderModal ? "none" : "block"}>
            <FAQ />
          </Box>
        </VStack>

        <ActiveCardModalDisplay 
          activeCard={activeCard} 
          containerRef={containerRef} 
          onClose={() => setActiveCard(null)}
        />

      </Box>

      <style jsx global>{`
        @keyframes App-logo-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Box>
  );
};

export default Home;
